/**
 * Client for the `/ops/*` aggregate routes (axetay/really-enrich#40/#56, merged to main, now
 * living in packages/api as Hono routes). All functions below call the real backend via
 * `apiFetch` -- this file used to be per-screen MOCK data before the 5 dashboard screens were
 * merged together; each screen's original function names/signatures are kept unchanged here so
 * no page component needed editing, only this service file.
 */

// Namespace import, not a named one: `@axetay/really-enrich-models` is a pure-CJS package whose
// `Outcome` reaches its root export via a two-level __exportStar chain
// (index -> enrichment-run/index -> outcome) -- Vite's CJS/ESM interop can't statically resolve a
// named export through that (`does not provide an export named 'Outcome'` at runtime, both `vite
// dev` and `vite build`). A namespace import sidesteps the problem: the whole `module.exports`
// object becomes the namespace, so no per-name static analysis is needed.
import * as ReallyEnrichModels from '@axetay/really-enrich-models';

import { apiFetch, apiFetchOptional, buildQueryString } from './apiClient';

// A `const` alone only re-exports the value; TS enums are also a type, so pair it with an explicit
// type alias of the same name (value-space and type-space are distinct, so this is not a conflict)
// -- every downstream file that uses `Outcome` as a type annotation (e.g. `Record<Outcome, ...>`)
// needs that, not just the value.
const Outcome = ReallyEnrichModels.Outcome;
type Outcome = ReallyEnrichModels.Outcome;

export { Outcome };

// ---------------------------------------------------------------------------
// Shared scope metadata (Runs + Cost + Latency + Coverage + Overview screens)
// ---------------------------------------------------------------------------

/** The real enrichment scopes, in the exact `EnrichmentScope` enum casing the backend actually
 * returns/expects (e.g. "AGENT_SOURCE_LISTING") -- this must match, since `/ops/runs?scope=` is
 * an exact string comparison server-side. INTEGRATION_TEST is included because it shows up in
 * real run data (a synthetic scope used to validate the pipeline itself), even though it's
 * excluded from packages/data-schemas' own ENRICHMENT_SCOPES (that list is about which scopes
 * have a real per-scope enrichment API route, not which scopes produce runs worth seeing here). */
export const ENRICHMENT_SCOPES = [
  'EPC',
  'TRANSPORT',
  'FLOOR_PLAN',
  'IMAGE_DESCRIPTIONS',
  'FEATURE_EXTRACTION',
  'AGENT_SOURCE_LISTING',
  'LOCATION',
  'RECURRING_CHARGES',
  'ASKING_PRICE_HISTORY',
  'PROPERTY_IDENTITY',
  'AMENITIES',
  'IMAGE_EMBEDDINGS',
  'INTEGRATION_TEST',
] as const;

export type EnrichmentScope = (typeof ENRICHMENT_SCOPES)[number];

/** A few scopes don't read well as Title Case of their underscore-split words. */
const SCOPE_LABEL_OVERRIDES: Partial<Record<EnrichmentScope, string>> = {
  EPC: 'EPC',
};

/**
 * Display label for a scope id (e.g. "AGENT_SOURCE_LISTING" -> "Agent Source Listing"). Falls
 * back to a generic underscore-split Title Case for anything not in the override table above, so
 * an unrecognized/future scope (or one this dashboard doesn't otherwise know about) still gets a
 * reasonable label instead of the raw enum string -- rather than only working for an explicit list.
 */
export function scopeLabel(scope: string): string {
  const override = SCOPE_LABEL_OVERRIDES[scope as EnrichmentScope];
  if (override) return override;
  return scope
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Display label for a run outcome (e.g. "NO_DATA_FOUND" -> "No data found"), sentence case rather
 * than scopeLabel's Title Case. Deliberately no per-value override table: any `Outcome` member --
 * including ones added later (axetay/really#67 tracks extending the enum) -- reads fine through
 * this generic split, so the Runs outcome filter (built from `Object.values(Outcome)`) never needs
 * a matching label update.
 */
export function outcomeLabel(outcome: string): string {
  const words = outcome.split('_').filter((word) => word.length > 0).map((word) => word.toLowerCase());
  if (words.length === 0) return outcome;
  return [words[0][0].toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
}

/** Shared cost-amount formatter -- Cost, Overview, and Runs all show real spend down to
 * $0.00001 (a deterministic-scope run typically costs a fraction of a cent), but with no padded
 * trailing zeros: `maximumFractionDigits` bounds precision, `minimumFractionDigits: 0` lets
 * Intl.NumberFormat trim them (formatCostUsd(8) -> "$8", not "$8.00000"). */
const costFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 5,
});

export function formatCostUsd(amountUsd: number): string {
  return costFormatter.format(amountUsd);
}

// ---------------------------------------------------------------------------
// GET /ops/stats -- Overview, Latency, Cost
// ---------------------------------------------------------------------------

export type StatsWindow = '1h' | '24h' | '7d' | 'today' | '30d' | 'all';

/** One row of `GET /ops/stats` -- aggregated run stats for a single scope over a single window. */
export interface StatsRow {
  window: string;
  scope: string;
  totalRuns: number;
  successRuns: number;
  failedRuns: number;
  skippedRuns: number;
  successRate: number;
  p50DurationMs: number;
  p95DurationMs: number;
  totalCostUsd: number;
  /** Portion of totalCostUsd that was upstream model inference, vs. non-inference fees
   * (e.g. OpenRouter's PDF file-processing fee). */
  totalUpstreamInferenceCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  /** ISO timestamp of the most recent run in that scope/window, or null. */
  lastRunAt: string | null;
}

export interface StatsResponse {
  items: StatsRow[];
}

export interface FetchStatsParams {
  /** Required unless `since` is given -- matches the backend's own "window or since" contract. */
  window?: StatsWindow;
  /** Explicit ISO-8601 custom-range bounds -- take precedence over `window` on the backend when
   * `since` is present. Set both for a bounded range, or just one for an open-ended range. */
  since?: string;
  until?: string;
  scope?: string;
}

export async function fetchStats(params: FetchStatsParams): Promise<StatsResponse> {
  return apiFetch<StatsResponse>(`/ops/stats?${buildQueryString({ ...params })}`);
}

/** Cost screen's original name/signature for the same route -- kept as-is, no page edits needed. */
export async function getStats(window: StatsWindow = '24h', scope?: string): Promise<StatsResponse> {
  return fetchStats({ window, scope });
}

// ---------------------------------------------------------------------------
// GET /ops/daily -- Cost
// ---------------------------------------------------------------------------

export interface DailyRow {
  scope: string;
  day: string; // ISO date (YYYY-MM-DD)
  totalRuns: number;
  successRuns: number;
  failedRuns: number;
  totalCostUsd: number;
  avgDurationMs: number;
}

export interface DailyStatsResponse {
  items: DailyRow[];
}

export interface DailyCostTotal {
  day: string;
  totalCostUsd: number;
}

/**
 * Sums `totalCostUsd` across every scope for each day, so the Cost trend line has one point
 * per day rather than one per (scope, day). GET /ops/daily returns scope x day grain.
 */
export function aggregateDailyCostTotals(items: DailyRow[]): DailyCostTotal[] {
  const totals = new Map<string, number>();
  for (const row of items) {
    totals.set(row.day, Math.round(((totals.get(row.day) ?? 0) + row.totalCostUsd) * 100_000) / 100_000);
  }
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, totalCostUsd]) => ({ day, totalCostUsd }));
}

/** Ranks stats rows by `totalCostUsd` descending, highest spend first. */
export function rankByCost(items: StatsRow[]): StatsRow[] {
  return [...items].sort((a, b) => b.totalCostUsd - a.totalCostUsd);
}

/** GET /ops/daily?days=<days> -- per-scope, per-day run counts, cost and duration. */
export async function getDailyStats(days = 14): Promise<DailyStatsResponse> {
  return apiFetch<DailyStatsResponse>(`/ops/daily?${buildQueryString({ days })}`);
}

// ---------------------------------------------------------------------------
// GET /ops/coverage -- Overview, Coverage
// ---------------------------------------------------------------------------

/** A single scope's lifetime enrichment outcome mix, as returned by GET /ops/coverage. */
export interface CoverageRow {
  scope: string;
  /** Properties with at least one successful record. Equals updatedCount + noChangeCount + noDataFoundCount. */
  attemptedProperties: number;
  updatedCount: number;
  noChangeCount: number;
  noDataFoundCount: number;
  /** Every distinct property this scope has ever been triggered for, success or not -- the
   * coverage denominator. */
  totalIngested: number;
  /** `totalIngested - attemptedProperties`: ingested but not yet successfully enriched (sampled
   * out, failing every attempt, or still pending). */
  neverRunCount: number;
}

export interface CoverageResponse {
  items: CoverageRow[];
}

export async function fetchCoverage(): Promise<CoverageResponse> {
  return apiFetch<CoverageResponse>('/ops/coverage');
}

// ---------------------------------------------------------------------------
// GET /ops/runs -- Runs
// ---------------------------------------------------------------------------

/** One structured log line emitted during a run. */
export interface RunLogMessage {
  level: string;
  message: string;
  timestamp: string;
}

/** One row of the `/ops/runs` ledger. Mirrors `EnrichmentRunItem`. */
export interface RunRow {
  runId: string;
  trigger: string;
  scope: string;
  propertyId: string;
  status: string;
  result?: string;
  message?: string;
  error?: string;
  durationMs?: number;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  /** Portion of costUsd that was upstream model inference, when the provider itemizes it. */
  upstreamInferenceCostUsd?: number;
  /** OpenRouter's own generation id -- the real join key back to its `/generation` record. */
  providerGenerationId?: string;
  /** Raw provider usage/routing data behind costUsd/inputTokens/outputTokens. */
  providerMetadata?: unknown;
  logMessages?: RunLogMessage[];
  createdAt: string;
}

/**
 * `items` is capped server-side (500, no offset/cursor pagination); `total` is the full
 * filtered count and can exceed `items.length` on broad filters.
 */
export interface RunsResponse {
  items: RunRow[];
  total: number;
}

export type DateWindow = 'today' | '7d' | '30d' | 'all';
export type RunStatus = 'success' | 'failed' | 'skipped';
/** Cost band filter keys -- these are the values sent to the API, not display labels. */
export type CostBand = 'free' | 'under-0.01' | '0.01-0.05' | '0.05-0.20' | 'over-0.20';

export interface RunsQueryParams {
  window?: DateWindow;
  /** Explicit ISO-8601 custom-range bounds -- take precedence over `window` on the backend when
   * `since` is present. Set both for a bounded range, or just one for an open-ended range. */
  since?: string;
  until?: string;
  scope?: string;
  status?: RunStatus;
  /** Exact match against the run's `result` field -- an `Outcome` enum value, distinct from
   * `status` (success/failed/skipped). */
  outcome?: string;
  propertyId?: string;
  /** Exact match against the run's own `runId` (the ledger primary key) -- see {@link fetchRunById}. */
  runId?: string;
  costBand?: CostBand;
}

export async function fetchRuns(params: RunsQueryParams = {}): Promise<RunsResponse> {
  return apiFetch<RunsResponse>(`/ops/runs?${buildQueryString({ ...params })}`);
}

/**
 * All runs for one exact (scope, propertyId) -- the Enrichments screen's "view runs" modal.
 * `/ops/runs`'s own `propertyId` filter is a substring ILIKE search (built for the Runs screen's
 * free-text box), so this filters the response down to an exact match rather than adding a second,
 * stricter query param to the backend for a single caller.
 */
export async function fetchRunsForEnrichment(scope: string, propertyId: string): Promise<RunRow[]> {
  const response = await fetchRuns({ scope, propertyId, window: 'all' });
  return response.items.filter((row) => row.propertyId === propertyId);
}

/**
 * The single ledger row for one runId -- backs the Run detail screen. `runId` is the ledger's
 * primary key, so at most one row can ever match; `window: 'all'` since the run being looked up
 * could be arbitrarily old. Returns undefined rather than throwing when nothing matches (a bad/stale
 * link), letting the caller render a "not found" state instead of an error state.
 */
export async function fetchRunById(runId: string): Promise<RunRow | undefined> {
  const response = await fetchRuns({ runId, window: 'all' });
  return response.items[0];
}

// ---------------------------------------------------------------------------
// GET /ops/enrichments -- Enrichments
// ---------------------------------------------------------------------------

/** One row of the `/ops/enrichments` listing -- the current-state record store, one row per
 * (scope, propertyId), NOT a per-run ledger entry (contrast `RunRow`). */
export interface EnrichmentListRow {
  scope: string;
  propertyId: string;
  result: string;
  inputHash?: string;
  /** Unix seconds -- when this record was last (over)written by a successful run. */
  timestamp: number;
  /** Unix seconds -- when the LISTING_ENRICHED event for this record was emitted, if it has been. */
  publishedAt?: number;
}

/** Same "items capped, total is the full filtered count" contract as `RunsResponse`. */
export interface EnrichmentsResponse {
  items: EnrichmentListRow[];
  total: number;
}

export interface EnrichmentsQueryParams {
  window?: DateWindow;
  since?: string;
  until?: string;
  scope?: string;
  outcome?: string;
  propertyId?: string;
}

export async function fetchEnrichments(params: EnrichmentsQueryParams = {}): Promise<EnrichmentsResponse> {
  return apiFetch<EnrichmentsResponse>(`/ops/enrichments?${buildQueryString({ ...params })}`);
}

// ---------------------------------------------------------------------------
// GET /enrichment/{scope} -- per-property, per-scope stored enrichment record
// (input/output/prompt/model/cost), for the Run detail view. Same route
// really-data-ui's Property Inspector reads (axetay/really, packages/really-data/ui).
// ---------------------------------------------------------------------------

/** Scopes with a real `/enrichment/{scope}` route (packages/data-schemas' own
 * ENRICHMENT_SCOPES) -- INTEGRATION_TEST is a synthetic pipeline-validation scope with no
 * per-scope table/route of its own, so it's deliberately excluded here. */
const ENRICHMENT_DETAIL_SCOPES = new Set<EnrichmentScope>([
  'EPC',
  'TRANSPORT',
  'FLOOR_PLAN',
  'IMAGE_DESCRIPTIONS',
  'FEATURE_EXTRACTION',
  'AGENT_SOURCE_LISTING',
  'LOCATION',
  'RECURRING_CHARGES',
  'ASKING_PRICE_HISTORY',
  'PROPERTY_IDENTITY',
  'AMENITIES',
  'IMAGE_EMBEDDINGS',
]);

export function hasEnrichmentDetailRoute(scope: string): boolean {
  return ENRICHMENT_DETAIL_SCOPES.has(scope as EnrichmentScope);
}

/** One part of a multimodal prompt sent to the model. Mirrors really-data-ui's `PromptPart`. */
export interface PromptPart {
  type: 'text' | 'image' | 'pdf';
  text?: string;
  ref?: string;
  mediaType?: string;
}

/** Resolved enricher input, and -- for AI enrichers -- which model ran, token usage, cost,
 * timing, and (optionally) the prompt that produced the output. `model` is absent for
 * deterministic enrichers, which make no AI call. */
export interface EnrichmentMetadata {
  input?: unknown;
  model?: { requested: string; used: string };
  usage?: { inputTokens: number; outputTokens: number };
  cost?: {
    usd: number;
    usCents: number;
    /** Portion of usd that was upstream model inference, when the provider itemizes it. */
    upstreamInferenceCostUsd?: number;
  };
  /** Raw provider usage/routing data behind cost/usage. */
  providerMetadata?: unknown;
  /** OpenRouter's own generation id -- the real join key back to its `/generation` record. */
  providerGenerationId?: string;
  attempts?: number;
  latencyMs?: number;
  traceId?: string;
  prompt?: { system?: string; content: PromptPart[] };
  promptOmitted?: boolean;
}

/** The full stored enrichment record for a property/scope -- the LATEST one, not necessarily the
 * one a specific historical run produced (the store is overwritten on each new run for the same
 * property/scope; there is no per-run history here, only a per-run ledger row in `/ops/runs`). */
export interface EnrichmentRecord {
  output?: unknown;
  result?: string;
  metadata?: EnrichmentMetadata;
  inputHash?: string;
  /** Unix seconds. */
  timestamp?: number;
  propertyId?: string;
  /** Unix seconds. */
  publishedAt?: number;
}

interface EnrichmentRecordResponse {
  items: EnrichmentRecord[];
}

/** Fetches the current stored enrichment record for a property/scope, or `null` when none exists
 * (e.g. every run for this property/scope so far has failed or been skipped -- only a successful
 * run ever writes a record). `null`, not `undefined`: TanStack Query v5 treats a queryFn resolving
 * to `undefined` as an error, not a valid "no data" result.
 *
 * Calls the Cognito-gated /ops/enrichment-record, not the API-key-gated /enrichment/{scope} the
 * really-data explorer uses -- the dashboard already has a real JWT from its own OIDC login and
 * shouldn't need a second, publicly-shipped credential for its own routes (#95). `scope` here is
 * the real EnrichmentScope enum casing (e.g. "EPC"), matching /ops/*'s own scope-param convention
 * (unlike /enrichment/{scope}'s kebab-case route segment). */
export async function fetchEnrichmentRecord(scope: string, propertyId: string): Promise<EnrichmentRecord | null> {
  const response = await apiFetchOptional<EnrichmentRecordResponse>(
    `/ops/enrichment-record?${buildQueryString({ scope, propertyId })}`,
  );
  return response?.items[0] ?? null;
}
